// Query keys for data inside one tenant (docs/web/conventions.md §3.1).
//
// Every query that reads `/v1/tenants/{id}/...` nests its key under
// `tenantScopeKey(id)`, for example `['tenant', id, 'users']`. Ending a
// grant, a grant expiring, or logging out removes the whole prefix in one
// call, so no clinic's data outlives the access that fetched it.
//
// Keys hold ids only. Never a grant token.

export function tenantScopeKey(tenantId: string): readonly ['tenant', string] {
  return ['tenant', tenantId.toLowerCase()] as const;
}

/**
 * Tenant lifecycle keys. They live here, not in the tenants feature,
 * because the shell reads clinic names from the cache too (banner, step-up
 * dialog). Summaries and checks hold no PHI.
 */
export const tenantKeys = {
  /** `GET /v1/tenants`. Filtered in the browser, so one key. */
  list: ['tenants'] as const,
  /** `GET /v1/tenants/:tenantId`. */
  summary: (tenantId: string) => [...tenantScopeKey(tenantId), 'summary'] as const,
  /** `GET /v1/tenants/:tenantId/activation-checks`. Needs a grant. */
  activationChecks: (tenantId: string) =>
    [...tenantScopeKey(tenantId), 'activation-checks'] as const,
  /** `GET /v1/tenants/:tenantId/users`. Staff emails and names, not PHI. */
  users: (tenantId: string) => [...tenantScopeKey(tenantId), 'users'] as const,
  /** `GET /v1/tenants/:tenantId/roles`. */
  roles: (tenantId: string) => [...tenantScopeKey(tenantId), 'roles'] as const,
  /**
   * `GET /v1/tenants/:tenantId/emr-instances`. Names, ids and key ids only.
   * Never a secret: those stay in the secret dialog's state.
   */
  emrInstances: (tenantId: string) => [...tenantScopeKey(tenantId), 'emr-instances'] as const,
  /**
   * `GET /v1/tenants/:tenantId/emr-instances/:instanceId/keys`. Key ids,
   * statuses and times only. It sits under `emrInstances`, so refreshing
   * the instances refreshes every key list too.
   */
  emrInstanceKeys: (tenantId: string, instanceId: string) =>
    [...tenantScopeKey(tenantId), 'emr-instances', instanceId, 'keys'] as const,
  /**
   * `GET /v1/tenants/:tenantId/idis2go-links`. Customer names, start points
   * and backfill counts. No patient data.
   */
  idis2goLinks: (tenantId: string) => [...tenantScopeKey(tenantId), 'idis2go-links'] as const,
  /**
   * `GET /v1/tenants/:tenantId/integration-status`. Counts, times, link ids
   * and EMR instance names. No patient data.
   */
  integrationStatus: (tenantId: string) =>
    [...tenantScopeKey(tenantId), 'integration-status'] as const,
};
